import styled from '@emotion/styled';
import { Button, DatePicker, Input, Select, Table } from 'antd';
import { orderBy, sumBy } from 'lodash';
import moment, { Moment } from 'moment';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FaDownload } from 'react-icons/fa';
import { FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { CompteEntity } from '../../../../entities/compte.entity';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';
import { ExportFormatButton } from '../../../../shared/ExportFormatButton';
import { getExportMetadata } from '../../../../shared/exportFiles';
import { Layout } from '../../../shared/Layout';
import { StatusPill } from '../../shared/StatusPill';
import { exportCompteData } from '../components/exportDataExcel';
import { getComptes } from '../network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 90% 8%, rgba(255, 176, 0, 0.08), transparent 20%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .bread-crum span {
    color: var(--mt-text);
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-text);
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
      color: var(--mt-text);
    }
  }

  .content {
    padding: 12px;
    width: 100%;
    color: var(--mt-text);
  }

  .account-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .account-hero h1 {
    margin: 5px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .account-hero p {
    max-width: 760px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.5;
  }

  .hero-actions,
  .page-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .ghost-action,
  .primary-action {
    border-radius: 999px;
    font-family: Poppins-B;
  }

  .ghost-action {
    border-color: rgba(255, 176, 0, 0.28);
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
  }

  .primary-action {
    border: 0;
    background: linear-gradient(135deg, #ff4f1f, ${YELLOW});
    color: #07141c;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .toolbar,
  .table-shell {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 78px;
    padding: 13px;
  }

  .kpi-card span {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .kpi-card strong {
    display: block;
    margin-top: 6px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 21px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .toolbar {
    display: grid;
    grid-template-columns: minmax(280px, 460px) 1fr;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding: 12px;
  }

  .search-box {
    position: relative;
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 12px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    padding-left: 36px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .filter-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(130px, 1fr));
    gap: 8px;
    margin-top: 10px;
  }

  .filter-grid .ant-select-selector,
  .filter-grid .ant-picker {
    min-height: 38px;
    border-color: var(--mt-border) !important;
    border-radius: 8px !important;
    background: var(--mt-surface) !important;
    color: var(--mt-text) !important;
  }

  .filter-grid .ant-select-selection-item,
  .filter-grid .ant-picker-input input {
    color: var(--mt-text) !important;
  }

  .filter-grid .ant-select-selection-placeholder,
  .filter-grid .ant-picker-input input::placeholder {
    color: var(--mt-muted) !important;
  }

  .table-shell {
    margin-top: 12px;
    overflow: hidden;
  }

  .table-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 46px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY};
    color: ${YELLOW};
    font-family: Poppins-B;
    text-transform: uppercase;
  }

  .table-count {
    border: 1px solid rgba(255, 176, 0, 0.25);
    border-radius: 999px;
    padding: 5px 9px;
    color: #fff7e8;
    font-size: 11px;
    text-transform: none;
  }

  .account-table .ant-table {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .account-table .ant-table-thead > tr > th {
    border-bottom: 1px solid var(--mt-border);
    font-size: 11px;
    text-transform: uppercase;
  }

  .account-table .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--mt-border);
    color: var(--mt-text);
  }

  .account-cell,
  .code-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .account-cell strong,
  .code-cell strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .account-cell span,
  .code-cell span {
    color: var(--mt-muted);
    font-size: 11px;
  }

  .amount-cell {
    display: block;
    text-align: right;
    font-family: Poppins-B;
    white-space: nowrap;
  }

  .amount-cell.credit {
    color: #00965d;
  }

  .amount-cell.debit {
    color: #dc2626;
  }

  .amount-cell.balance {
    color: var(--mt-warning);
  }

  @media (max-width: 760px) {
    .content {
      padding: 18px 12px;
    }

    .account-hero,
    .toolbar {
      align-items: stretch;
      display: flex;
      flex-direction: column;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }

    .filter-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const KpiCard = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
}) => (
  <div className='kpi-card'>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
    <div className='icon'>{icon}</div>
  </div>
);

export const ComptePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [comptes, setComptes] = useState<CompteEntity[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [chapterFilter, setChapterFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState<[Moment, Moment] | null>(null);
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  const fetchData = () => {
    setIsLoading(true);
    getComptes(connectedUser.id).then(({ data }) => {
      setComptes(orderBy(data, 'num_compte', 'asc'));
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredComptes = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return comptes.filter((cpt) =>
      {
        const searchable = [
        cpt.num_compte,
        cpt.intitule,
        cpt.code_chapitre,
        cpt.code_titre,
        cpt.code_svt,
        cpt.code_client,
        cpt.code_agence,
        cpt.statut,
      ]
        .filter(Boolean)
        .join(' ')
          .toLowerCase();
        const createdAt = moment(cpt.created_at);
        const matchesSearch = !term || searchable.includes(term);
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && isActiveStatus(cpt.statut)) ||
          (statusFilter === 'inactive' && !isActiveStatus(cpt.statut));
        const matchesChapter =
          chapterFilter === 'all' || cpt.code_chapitre === chapterFilter;
        const matchesType =
          accountTypeFilter === 'all' ||
          (accountTypeFilter === 'client' && Boolean(cpt.code_client)) ||
          (accountTypeFilter === 'titre' && Boolean(cpt.code_titre)) ||
          (accountTypeFilter === 'svt' && Boolean(cpt.code_svt));
        const matchesDate =
          !dateRange ||
          (createdAt.isValid() &&
            createdAt.isSameOrAfter(dateRange[0], 'day') &&
            createdAt.isSameOrBefore(dateRange[1], 'day'));

        return (
          matchesSearch &&
          matchesStatus &&
          matchesChapter &&
          matchesType &&
          matchesDate
        );
      },
    );
  }, [accountTypeFilter, chapterFilter, comptes, dateRange, searchTerm, statusFilter]);

  const chapterOptions = useMemo(
    () => Array.from(new Set(comptes.map((cpt) => cpt.code_chapitre).filter(Boolean))),
    [comptes],
  );

  const accountStats = useMemo(
    () => ({
      active: filteredComptes.filter((cpt) => isActiveStatus(cpt.statut)).length,
      credit: sumBy(filteredComptes, 'credit'),
      debit: sumBy(filteredComptes, 'debit'),
      solde: sumBy(filteredComptes, 'solde'),
      total: filteredComptes.length,
    }),
    [filteredComptes],
  );

  return (
    <Layout>
      <Container>
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <section className='account-hero'>
            <div>
              <span className='eyebrow'>Comptabilité titres</span>
              <h1>Comptes</h1>
              <p>
                Pilotez le plan des comptes, les soldes et les rattachements
                titres, clients, agences et SVT depuis un registre unique.
              </p>
            </div>
            <div className='hero-actions'>
              <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={fetchData}>
                Rafraîchir
              </Button>
              <ExportFormatButton
                className='primary-action'
                onExport={(format) => exportCompteData(filteredComptes, format, getExportMetadata(connectedUser))}
              />
            </div>
          </section>

          <section className='kpi-grid'>
            <KpiCard label='Comptes affichés' value={accountStats.total || '--'} icon={<FaDownload />} />
            <KpiCard label='Comptes actifs' value={accountStats.active || '--'} icon={<FaDownload />} />
            <KpiCard
              label='Solde net'
              value={`${formatNum(accountStats.solde) || '--'} XAF`}
              icon={<FaDownload />}
            />
            <KpiCard
              label='Débit / crédit'
              value={`${formatNum(accountStats.debit) || '--'} / ${formatNum(accountStats.credit) || '--'}`}
              icon={<FaDownload />}
            />
          </section>

          <section className='toolbar'>
            <div>
              <div className='search-box'>
                <FiSearch />
                <Input
                  placeholder='Rechercher compte, titre, client, agence...'
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              <div className='filter-grid'>
                <Select value={statusFilter} onChange={setStatusFilter}>
                  <Select.Option value='all'>Tous statuts</Select.Option>
                  <Select.Option value='active'>Actifs</Select.Option>
                  <Select.Option value='inactive'>Inactifs</Select.Option>
                </Select>
                <Select value={chapterFilter} onChange={setChapterFilter}>
                  <Select.Option value='all'>Tous chapitres</Select.Option>
                  {chapterOptions.map((chapter) => (
                    <Select.Option key={chapter} value={chapter}>
                      {chapter}
                    </Select.Option>
                  ))}
                </Select>
                <Select value={accountTypeFilter} onChange={setAccountTypeFilter}>
                  <Select.Option value='all'>Tous rattachements</Select.Option>
                  <Select.Option value='client'>Client</Select.Option>
                  <Select.Option value='titre'>Titre</Select.Option>
                  <Select.Option value='svt'>SVT</Select.Option>
                </Select>
                <DatePicker.RangePicker
                  value={dateRange as any}
                  onChange={(value) => setDateRange(value as [Moment, Moment] | null)}
                  format='DD/MM/YYYY'
                />
              </div>
            </div>
            <div className='page-toolbar'>
              <span className='table-count'>{filteredComptes.length} comptes affichés</span>
            </div>
          </section>

          <section className='table-shell'>
            <div className='table-title'>
              <span>Registre des comptes</span>
              <span className='table-count'>{comptes.length} comptes total</span>
            </div>
            <Table
              columns={COLUMNS}
              dataSource={filteredComptes}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              className='account-table'
              size='small'
              loading={isLoading}
              scroll={{ x: 1100 }}
              pagination={false}
            />
          </section>
        </div>
      </Container>
    </Layout>
  );
};

const COLUMNS = [
  {
    title: 'Compte',
    width: 280,
    render: (_cell: any, row: CompteEntity) => (
      <div className='account-cell'>
        <strong>{row.num_compte || '--'}</strong>
        <span>{row.intitule || '--'}</span>
      </div>
    ),
  },
  {
    title: 'Rattachements',
    width: 220,
    render: (_cell: any, row: CompteEntity) => (
      <div className='code-cell'>
        <strong>Titre {row.code_titre || '--'}</strong>
        <span>
          Client {row.code_client || '--'} · Agence {row.code_agence || '--'}
        </span>
      </div>
    ),
  },
  {
    title: 'Chapitre / SVT',
    width: 180,
    render: (_cell: any, row: CompteEntity) => (
      <div className='code-cell'>
        <strong>{row.code_chapitre || '--'}</strong>
        <span>SVT {row.code_svt || '--'}</span>
      </div>
    ),
  },
  {
    title: 'Crédit',
    dataIndex: 'credit',
    render: (cell: any) => (
      <span className='amount-cell credit'>{formatNum(cell) || '--'}</span>
    ),
    align: 'right' as const,
  },
  {
    title: 'Débit',
    dataIndex: 'debit',
    render: (cell: any) => (
      <span className='amount-cell debit'>{formatNum(cell) || '--'}</span>
    ),
    align: 'right' as const,
  },
  {
    title: 'Solde',
    dataIndex: 'solde',
    render: (cell: any) => (
      <span className='amount-cell balance'>{formatNum(cell) || '--'}</span>
    ),
    align: 'right' as const,
  },
  {
    title: 'Dernier crédit',
    dataIndex: 'date_dernier_credit',
    render: (cell: any) =>
      cell ? `${moment(cell).format('DD/MM/YYYY')}` : '--',
  },
  {
    title: 'Dernier débit',
    dataIndex: 'date_dernier_debit',
    render: (cell: any) =>
      cell ? `${moment(cell).format('DD/MM/YYYY')}` : '--',
  },
  {
    title: 'Statut',
    dataIndex: 'statut',
    align: 'center' as const,
    render: (cell: string) => (
      <StatusPill tone={isActiveStatus(cell) ? 'success' : 'neutral'}>
        {isActiveStatus(cell) ? 'Actif' : cell || '--'}
      </StatusPill>
    ),
  },
  {
    title: 'Date création',
    dataIndex: 'created_at',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
];

const isActiveStatus = (status?: string) => {
  const normalized = `${status || ''}`.toLowerCase();

  return ['o', 'oui', 'actif', 'active', '1', 'true'].includes(normalized);
};
